import { useCallback, useEffect, useRef, useState } from 'react';
import { useKeycloak } from '../components/KeycloakProvider';
import { useApiClient } from './useApiClient';
import {
  DEFAULT_GROUPING_CONFIG,
  DEFAULT_VIEW_TYPE,
  GenerateMatrixResponse,
  getDefaultDateRange,
  ReportData,
  ReportFilters,
} from '../types/reports';
import { isToday } from '../utils/dateUtils';

// Debounce delay for date range changes (ms)
const DATE_CHANGE_DEBOUNCE_MS = 500;

// Validate date format YYYY-MM-DD
const isValidDate = (dateStr: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
};

interface UseReportsDataParams {
  filters?: Partial<ReportFilters>;
  enabled?: boolean;
}

interface ReportError {
  id: number;
  message: string;
}

interface UseReportsDataReturn {
  reportData: ReportData | null;
  loading: boolean;
  error: ReportError | null;
  filters: ReportFilters;
  updateFilters: (filters: Partial<ReportFilters>) => void;
  refresh: () => Promise<void>;
}

export const useReportsData = ({
  filters: initialFilters,
  enabled = true,
}: UseReportsDataParams = {}): UseReportsDataReturn => {
  const { keycloak, initialized } = useKeycloak();
  const apiClient = useApiClient();

  const [filters, setFilters] = useState<ReportFilters>({
    dateRange: initialFilters?.dateRange || getDefaultDateRange(),
    groupingConfig: initialFilters?.groupingConfig || DEFAULT_GROUPING_CONFIG,
    viewType: initialFilters?.viewType || DEFAULT_VIEW_TYPE,
    selectedUsers: initialFilters?.selectedUsers,
    selectedProjects: initialFilters?.selectedProjects,
    selectedLabels: initialFilters?.selectedLabels,
  });

  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ReportError | null>(null);
  const errorIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Build groupBy parameter from groupingConfig
  const buildGroupByParam = useCallback((config: typeof filters.groupingConfig): string => {
    return config.levels
      .filter((l) => l.enabled)
      .sort((a, b) => a.order - b.order)
      .map((l) => l.level)
      .join(',');
  }, []);

  const fetchReportData = useCallback(async () => {
    if (!initialized || !keycloak?.authenticated) {
      return;
    }

    // Abort previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    setError(null);

    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.set('startDate', filters.dateRange.startDate);
      params.set('endDate', filters.dateRange.endDate);
      params.set('groupBy', buildGroupByParam(filters.groupingConfig));
      params.set('timeZone', Intl.DateTimeFormat().resolvedOptions().timeZone);

      if (filters.selectedUsers && filters.selectedUsers.length > 0) {
        params.set('userIds', filters.selectedUsers.join(','));
      }

      if (filters.selectedProjects && filters.selectedProjects.length > 0) {
        params.set('projectIds', filters.selectedProjects.join(','));
      }

      if (filters.selectedLabels && filters.selectedLabels.length > 0) {
        params.set('labels', filters.selectedLabels.join(','));
      }

      // Call the new endpoint
      const response = await apiClient.get<GenerateMatrixResponse>(
        `/reports:generateMatrix?${params.toString()}`,
        { signal: abortController.signal }
      );

      // Map response to ReportData
      // Override isToday using local timezone (backend uses UTC)
      const columnsWithLocalToday = response.columns.map((col) => ({
        ...col,
        isToday: isToday(col.date),
      }));

      const reportData: ReportData = {
        dateRange: response.dateRange,
        groupingConfig: response.groupingConfig,
        rows: response.rows,
        columnDates: columnsWithLocalToday,
        grandTotal: {
          totalSeconds: response.grandTotal.totalSeconds,
          dailyTotals: response.grandTotal.dailyCells,
        },
      };

      setReportData(reportData);
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Failed to fetch report data:', err);
      errorIdRef.current += 1;
      setError({
        id: errorIdRef.current,
        message: err instanceof Error ? err.message : 'Failed to load report data',
      });
      // Clear data on error to show empty state
      setReportData(null);
    } finally {
      // Only update loading if this controller is still current
      if (abortControllerRef.current === abortController) {
        setLoading(false);
      }
    }
  }, [initialized, keycloak?.authenticated, apiClient, filters, buildGroupByParam]);

  const updateFilters = useCallback((newFilters: Partial<ReportFilters>) => {
    // Clear last fetched ref to allow new fetch
    lastFetchedFiltersRef.current = null;
    // Clear report data to prevent showing stale data
    setReportData(null);
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Track previous dateRange for debounce comparison
  const prevDateRangeRef = useRef(filters.dateRange);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Prevent double fetch in StrictMode - track last fetched filters
  const lastFetchedFiltersRef = useRef<string | null>(null);

  // Fetch report data when filters change or on initial load
  useEffect(() => {
    if (!enabled || !initialized || !keycloak?.authenticated) {
      return;
    }

    // Validate dates before fetching
    if (!isValidDate(filters.dateRange.startDate) || !isValidDate(filters.dateRange.endDate)) {
      return;
    }

    // Create a unique key for current filters to detect duplicates
    // Note: viewType is not included as it only affects display, not API fetch
    const filtersKey = JSON.stringify({
      dateRange: filters.dateRange,
      groupingConfig: filters.groupingConfig,
      selectedUsers: filters.selectedUsers,
      selectedProjects: filters.selectedProjects,
      selectedLabels: filters.selectedLabels,
    });

    // Skip if we already fetched with these exact filters (StrictMode protection)
    if (lastFetchedFiltersRef.current === filtersKey) {
      return;
    }

    // Check if only dateRange changed
    const dateRangeChanged =
      prevDateRangeRef.current.startDate !== filters.dateRange.startDate ||
      prevDateRangeRef.current.endDate !== filters.dateRange.endDate;

    // Update previous dateRange ref
    prevDateRangeRef.current = filters.dateRange;

    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If dateRange changed, debounce the fetch
    if (dateRangeChanged) {
      debounceTimerRef.current = setTimeout(() => {
        lastFetchedFiltersRef.current = filtersKey;
        fetchReportData();
      }, DATE_CHANGE_DEBOUNCE_MS);
    } else {
      // For other filter changes, fetch immediately
      lastFetchedFiltersRef.current = filtersKey;
      fetchReportData();
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Abort pending request on cleanup and reset last fetched ref
      // so StrictMode re-mount will fetch again
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        lastFetchedFiltersRef.current = null;
      }
    };
  }, [enabled, initialized, keycloak?.authenticated, filters, fetchReportData]);

  return {
    reportData,
    loading,
    error,
    filters,
    updateFilters,
    refresh: fetchReportData,
  };
};
